import { zodResolver } from "@hookform/resolvers/zod"
import { Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton } from "@mui/material"
import { CloseOutlined } from "@mui-symbols-material/w300"
import { useEffect } from "react"
import { FormProvider, useForm } from "react-hook-form"
import { useShallow } from "zustand/shallow"

import { usePackagingInfoSchema } from "../hooks/usePackagingInfoSchema"
import { type PackagingInfoFormValues } from "../schemas/packaging"
import { type PackagingInfo } from "../types/packaging"
import { serializeProductPackagingForm } from "../utils/packaging"

import { PackagingInfoForm } from "./PackagingInfoForm"

import { Button } from "@/components/common/Button"
import { useUnitOfMeasurementsStore } from "@/stores/unitOfMeasurmentsStore"

const initialDefaultValues: Partial<PackagingInfoFormValues> = {
  freightItem: null,
  freightClass: null,
  packageMetricWeight: undefined,
  palletSizeUom: undefined,
  form: undefined,
}

interface PackagingInfoModalProps {
  open: boolean
  onClose: () => void
  onSubmit: (data: PackagingInfo) => Promise<void>
  defaultValues?: Partial<Omit<PackagingInfoFormValues, "form">>
  loading?: boolean
}

export const PackagingInfoModal = ({
  open,
  onClose,
  onSubmit,
  defaultValues,
  loading,
}: PackagingInfoModalProps): React.ReactElement => {
  const packagingInfoSchema = usePackagingInfoSchema()

  const uoms = useUnitOfMeasurementsStore(useShallow((s) => s.units))

  const methods = useForm<PackagingInfoFormValues>({
    resolver: zodResolver(packagingInfoSchema),
    defaultValues: initialDefaultValues,
    values: defaultValues ? ({ ...initialDefaultValues, ...defaultValues } as PackagingInfoFormValues) : undefined,
    mode: "onChange",
  })

  const {
    handleSubmit,
    formState: { isValid, isDirty },
  } = methods

  useEffect(() => {
    methods.reset({ ...initialDefaultValues, ...defaultValues })
  }, [defaultValues, methods])

  const handleSave = handleSubmit((data) => {
    const { form, ...rest } = data

    const submitData = serializeProductPackagingForm(rest)

    return onSubmit(submitData)
  })

  const handleClose = () => {
    onClose()
    methods.reset()
  }

  return (
    <Dialog open={open} onClose={handleClose} fullWidth>
      <DialogTitle className='pb-3 pl-10 font-normal text-primary'>Packaging Information</DialogTitle>
      <IconButton
        aria-label='close'
        onClick={() => {
          methods.reset()
          onClose()
        }}
        className='absolute right-4 top-4 text-primary'
      >
        <CloseOutlined />
      </IconButton>
      <Divider />
      <FormProvider {...methods}>
        <DialogContent>
          <PackagingInfoForm uoms={uoms} disabled={loading} />
        </DialogContent>
        <DialogActions className='justify-between px-10 pb-10'>
          <Button appearance='outlined' onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!isValid || loading || !isDirty}>
            Save
          </Button>
        </DialogActions>
      </FormProvider>
    </Dialog>
  )
}
